import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  Typography,
  InputAdornment,
  Alert,
  MenuItem,
  Chip,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ColorSelector from './ColorSelector';
import CharacterOptions from './CharacterOptions';
import { GGDialog, GGDialogTitle } from './shared/StyledDialogs';
import { getAllMonsters } from '../services/monsterService';
import { getRoleInfo } from '../data/roles';
import { MONSTER_SIZE_MAP } from '../utils/tokenUtils';
import { useGame } from '../context/GameContext';

const ENEMY_TOKEN_COLOR = '#8B0000';
const MONSTER_PAGE_SIZE = 40;
const MONSTER_SCROLL_LOAD_THRESHOLD_PX = 220;

function formatChallengeRating(cr) {
  if (cr == null || cr === '') return '—';
  const map = { 0.125: '1/8', 0.25: '1/4', 0.5: '1/2' };
  if (map[cr] !== undefined) return map[cr];
  return String(cr);
}

function parseChallengeRating(cr) {
  if (cr == null || cr === '') return null;
  if (typeof cr === 'number') return Number.isFinite(cr) ? cr : null;
  if (typeof cr === 'string') {
    const trimmed = cr.trim();
    if (!trimmed) return null;
    if (trimmed.includes('/')) {
      const [num, den] = trimmed.split('/');
      const n = Number(num);
      const d = Number(den);
      if (Number.isFinite(n) && Number.isFinite(d) && d !== 0) return n / d;
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const CR_RANGE_OPTIONS = [
  { value: 'all', label: 'All CR' },
  { value: 'lt1', label: '<1' },
  { value: '1-2', label: '1-2' },
  { value: '3-4', label: '3-4' },
  { value: '5-8', label: '5-8' },
  { value: '9-12', label: '9-12' },
  { value: '13-16', label: '13-16' },
  { value: '17+', label: '17+' },
];

function matchesCrRange(cr, selectedRange) {
  if (selectedRange === 'all') return true;
  const value = parseChallengeRating(cr);
  if (value == null) return false;
  if (selectedRange === 'lt1') return value < 1;
  if (selectedRange === '17+') return value >= 17;
  const [minStr, maxStr] = selectedRange.split('-');
  const min = Number(minStr);
  const max = Number(maxStr);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return true;
  return value >= min && value <= max;
}

const CharacterModalContent = ({ onSelect, onBatchSelect, onClose, canManageEnemies = false }) => {
  const [isEnemy, setIsEnemy] = useState(false);
  const [isLarge, setIsLarge] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [characterName, setCharacterName] = useState('');
  const [characterNameTouched, setCharacterNameTouched] = useState(false);
  const [maxHealth, setMaxHealth] = useState('');
  const [armorClass, setArmorClass] = useState('');
  const [level, setLevel] = useState('');
  const [isPc, setIsPc] = useState(true);
  const [monsterSearch, setMonsterSearch] = useState('');
  const [monsterCrRange, setMonsterCrRange] = useState('all');
  const [monsterTypeFilter, setMonsterTypeFilter] = useState('all');
  const [monsterListVisibleCount, setMonsterListVisibleCount] = useState(MONSTER_PAGE_SIZE);
  const [queuedTokens, setQueuedTokens] = useState([]);
  const [selectedMonsterData, setSelectedMonsterData] = useState(null);

  const sortedMonsters = useMemo(() => {
    const all = getAllMonsters();
    return [...all].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, []);

  const monsterTypes = useMemo(() => {
    return [...new Set(sortedMonsters.map((m) => m.type).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [sortedMonsters]);

  const filteredMonsters = useMemo(() => {
    const q = monsterSearch.trim().toLowerCase();
    return sortedMonsters.filter((m) => {
      if (q && !m.name.toLowerCase().includes(q)) return false;
      if (!matchesCrRange(m.cr, monsterCrRange)) return false;
      if (monsterTypeFilter !== 'all' && m.type !== monsterTypeFilter) return false;
      return true;
    });
  }, [sortedMonsters, monsterSearch, monsterCrRange, monsterTypeFilter]);

  useEffect(() => {
    setMonsterListVisibleCount(MONSTER_PAGE_SIZE);
  }, [monsterSearch, monsterCrRange, monsterTypeFilter]);

  const visibleMonsters = useMemo(
    () => filteredMonsters.slice(0, monsterListVisibleCount),
    [filteredMonsters, monsterListVisibleCount]
  );

  const handleMonsterListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      const total = filteredMonsters.length;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining > MONSTER_SCROLL_LOAD_THRESHOLD_PX) return;
      setMonsterListVisibleCount((c) => {
        if (c >= total) return c;
        return Math.min(c + MONSTER_PAGE_SIZE, total);
      });
    },
    [filteredMonsters.length]
  );

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const size = 70;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');

          ctx.drawImage(img, 0, 0, size, size);
          const resizedImage = canvas.toDataURL('image/png');
          setSelectedImage(resizedImage);
          setImagePreview(resizedImage);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const resetFormInputs = useCallback(() => {
    setIsEnemy(false);
    setIsLarge(false);
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedColor(null);
    setCharacterName('');
    setCharacterNameTouched(false);
    setMaxHealth('');
    setArmorClass('');
    setLevel('');
    setIsPc(true);
    setSelectedMonsterData(null);
  }, []);

  const queueToken = useCallback((entry) => {
    setQueuedTokens((prev) => {
      const existing = prev.find((item) => item.key === entry.key);
      if (existing) {
        return prev.map((item) =>
          item.key === entry.key ? { ...item, count: item.count + 1 } : item
        );
      }
      return [...prev, { ...entry, count: 1 }];
    });
  }, []);

  const buildCustomTokenPayload = useCallback(() => {
    if (!selectedColor) return null;
    const levelNum =
      level === '' ? null : Math.min(20, Math.max(1, parseInt(level, 10) || 1));

    return {
      color: selectedColor,
      isEnemy,
      image: selectedImage,
      isLarge,
      name: characterName,
      maxHealth: maxHealth ? parseInt(maxHealth, 10) : null,
      armorClass: armorClass ? parseInt(armorClass, 10) : null,
      monsterData: isEnemy ? selectedMonsterData : null,
      level: isEnemy ? null : levelNum,
      isPc: isEnemy ? undefined : isPc,
    };
  }, [
    selectedColor,
    level,
    isEnemy,
    selectedImage,
    isLarge,
    characterName,
    maxHealth,
    armorClass,
    selectedMonsterData,
    isPc,
  ]);

  const handleQueueCustom = () => {
    setCharacterNameTouched(true);
    const payload = buildCustomTokenPayload();
    if (!payload) return;
    const key = `custom:${JSON.stringify(payload)}`;
    queueToken({
      key,
      label: payload.name?.trim() || (payload.isEnemy ? 'Enemy Token' : 'Character Token'),
      payload,
    });
    resetFormInputs();
  };

  const handleMonsterPick = (monster) => {
    if (!canManageEnemies) return;
    const monsterData = {
      index: monster.index,
      cr: monster.cr,
      xp: monster.xp,
      type: monster.type,
      role: monster.role,
    };
    // Mirror picked monster details into the left-side editor without queuing immediately.
    setIsEnemy(true);
    setIsLarge(MONSTER_SIZE_MAP[monster.size] || false);
    setSelectedImage(monster.img || null);
    setImagePreview(monster.img || null);
    setSelectedColor(ENEMY_TOKEN_COLOR);
    setCharacterName(monster.name);
    setMaxHealth(monster.hp != null ? String(monster.hp) : '');
    setArmorClass(monster.ac != null ? String(monster.ac) : '');
    setLevel('');
    setIsPc(false);
    setSelectedMonsterData(monsterData);
  };

  const handleQueuedCountChange = (key, delta) => {
    setQueuedTokens((prev) =>
      prev
        .map((item) => (item.key === key ? { ...item, count: Math.max(1, item.count + delta) } : item))
        .filter(Boolean)
    );
  };

  const handleRemoveQueuedToken = (key) => {
    setQueuedTokens((prev) => prev.filter((item) => item.key !== key));
  };

  const handleDone = () => {
    if (typeof onBatchSelect === 'function') {
      onBatchSelect(queuedTokens);
      return;
    }
    queuedTokens.forEach((entry) => {
      for (let i = 0; i < entry.count; i += 1) {
        const p = entry.payload;
        onSelect(
          p.color,
          p.isEnemy,
          p.image,
          p.isLarge,
          p.name,
          p.maxHealth,
          p.armorClass,
          p.monsterData,
          p.level,
          p.isPc,
          { keepModalOpen: true }
        );
      }
    });
    onClose();
  };

  const hasCharacterName = characterName.trim().length > 0;
  const showCharacterNameError = characterNameTouched && !hasCharacterName;
  const canAddToken = Boolean(selectedColor && hasCharacterName);

  return (
    <GGDialog open onClose={onClose} maxWidth='xl' fullWidth>
      <GGDialogTitle>Create Token</GGDialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 5 }}>
          {/* Custom */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexBasis: '50%'}}>
              <Divider sx={{ my: 1 }}>Character Details</Divider>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Character Name"
                  value={characterName}
                  onChange={(e) => setCharacterName(e.target.value)}
                  onBlur={() => setCharacterNameTouched(true)}
                  variant="outlined"
                  required
                  error={showCharacterNameError}
                  helperText={showCharacterNameError ? 'Name is required.' : ' '}
                  sx={{ flex: '1 1 100px', minWidth: 90, mb: 1}}
                />

                {!isEnemy && setIsPc && (
                  <TextField
                    label="Level (Optional)"
                    value={level}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d+$/.test(value)) {
                        const n = parseInt(value, 10);
                        if (value === '' || (n >= 1 && n <= 20)) {
                          setLevel(value);
                        }
                      }
                    }}
                    type="number"
                    variant="outlined"
                    inputProps={{ min: 1, max: 20 }}
                    sx={{ flex: '1 1 100px', minWidth: 90 }}
                    helperText="1–20, for encounter math"
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Max Health (Optional)"
                  value={maxHealth}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      setMaxHealth(value);
                    }
                  }}
                  type="number"
                  variant="outlined"
                  sx={{ flex: '1 1 120px', minWidth: 100 }}
                />
                <TextField
                  label="Armor Class (Optional)"
                  value={armorClass}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      setArmorClass(value);
                    }
                  }}
                  type="number"
                  variant="outlined"
                  sx={{ flex: '1 1 120px', minWidth: 100 }}
                />
                
              </Box>

              <CharacterOptions
                canManageEnemies={canManageEnemies}
                isEnemy={isEnemy}
                setIsEnemy={setIsEnemy}
                isLarge={isLarge}
                setIsLarge={setIsLarge}
                isPc={isPc}
                setIsPc={setIsPc}
              />

              <ColorSelector selectedColor={selectedColor} setSelectedColor={setSelectedColor} />

              <Divider sx={{ my: 1 }}>Custom Image</Divider>
              <Box>
                <TextField
                  type="file"
                  inputProps={{ accept: 'image/*' }}
                  onChange={handleImageUpload}
                  fullWidth
                  variant="outlined"
                />
                {imagePreview && (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

          {/* Monsters */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexBasis: '50%'}}>
              {!canManageEnemies && (
                <Alert severity="info">
                  Only a host or player with enemy management can place monster tokens from this list.
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  placeholder="Search monsters by name..."
                  value={monsterSearch}
                  onChange={(e) => setMonsterSearch(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ flex: '1 1 220px', minWidth: 180 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  label="CR"
                  value={monsterCrRange}
                  onChange={(e) => setMonsterCrRange(e.target.value)}
                  size="small"
                  sx={{ width: 120 }}
                >
                  {CR_RANGE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Type"
                  value={monsterTypeFilter}
                  onChange={(e) => setMonsterTypeFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 160, flex: '0 1 220px' }}
                >
                  <MenuItem value="all">All types</MenuItem>
                  {monsterTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {filteredMonsters.length} matching · showing {visibleMonsters.length} of{' '}
                {filteredMonsters.length} · alphabetical · scroll for more · click a row to load details
              </Typography>
              <List
                dense
                onScroll={handleMonsterListScroll}
                sx={{
                  maxHeight: 'min(52vh, 520px)',
                  overflow: 'auto',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: 'background.main',
                  py: 0,
                }}
              >
                {visibleMonsters.map((monster) => {
                  const roleInfo = getRoleInfo(monster.role);
                  return (
                    <ListItemButton
                      key={monster.index}
                      onClick={() => handleMonsterPick(monster)}
                      disabled={!canManageEnemies}
                      alignItems="flex-start"
                      sx={{
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 56, mt: 0.5 }}>
                        <Avatar
                          src={monster.img || undefined}
                          variant="rounded"
                          alt=""
                          imgProps={{ loading: 'lazy', decoding: 'async' }}
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'background.dark',
                            fontSize: '1rem',
                          }}
                        >
                          {!monster.img ? monster.name.charAt(0) : null}
                        </Avatar>
                      </ListItemAvatar>
                      <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontFamily: 'MedievalSharp', fontWeight: 600 }}
                          noWrap
                        >
                          {monster.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            columnGap: 1,
                            rowGap: 0.25,
                            alignItems: 'baseline',
                            mt: 0.5,
                          }}
                        >
                          <span>HP {monster.hp ?? '—'}</span>
                          <span>·</span>
                          <span>AC {monster.ac ?? '—'}</span>
                          <span>·</span>
                          <span>
                            {roleInfo.icon} {roleInfo.label}
                          </span>
                          <span>·</span>
                          <span>
                            { monster.type }
                          </span>
                          <span>·</span>
                          <span>CR {formatChallengeRating(monster.cr)}</span>
                          <span>·</span>
                          <span>{monster.xp != null ? `${monster.xp.toLocaleString()} XP` : '— XP'}</span>
                        </Typography>
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          </Box>
          <Button
            onClick={handleQueueCustom}
            disabled={!canAddToken}
            variant="outlined"
            fullWidth
            size="large"
          >
            Add Token
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mr: 'auto' }}>
            {queuedTokens.map((item) => (
              <Chip
                key={item.key}
                onDelete={() => handleRemoveQueuedToken(item.key)}
                label={(
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center', mr: 2}}>
                    <Typography variant="caption" sx={{ maxWidth: 180 }} noWrap>
                      {item.label}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ p: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQueuedCountChange(item.key, -1);
                        }}
                      >
                        <RemoveIcon fontSize="inherit" />
                      </IconButton>
                      <Typography variant="caption" sx={{ minWidth: 16, textAlign: 'center' }}>
                        {item.count}
                      </Typography>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ p: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQueuedCountChange(item.key, 1);
                        }}
                      >
                        <AddIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
                sx={{ height: 55 }}
              />
            ))}
          </Box>
          <Button onClick={handleDone} variant="contained" disabled={queuedTokens.length === 0}>
            Done
          </Button>
      </DialogActions>
    </GGDialog>
  );
};

const CharacterModal = () => {
  const {
    showColorModal,
    setShowColorModal,
    handleColorSelect,
    handleColorSelectBatch,
    canManageEnemies,
  } = useGame();
  if (!showColorModal) return null;
  return (
    <CharacterModalContent
      onSelect={handleColorSelect}
      onBatchSelect={handleColorSelectBatch}
      onClose={() => setShowColorModal(false)}
      canManageEnemies={canManageEnemies}
    />
  );
};

export default CharacterModal;
